import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeatureDetails, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Container,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Input,
	MultiSelect,
	Paragraph,
	Pill,
	Separator,
	Switch,
	UnorderedList,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import ProductTile from '../ProductTile';

type GoodHabitzConfig = {
	enabled: boolean;
	languageIDs: string[];
	hasApiKey: boolean;
	lastSyncedAt?: string | null;
};

function formatLastSyncedAt(iso: string): string {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return iso;
	}

	return new Intl.DateTimeFormat('nl-NL', {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(parsed);
}

const GOODHABITZ_LOGO_URL = '/images/marketplace/goodhabitz.png';

const Banner = styled(Box)`
	background: #ffffff;
	border: 1px solid ${tokens.border.subtle};
	border-radius: 8px;
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`;

const BannerLogo = styled.img`
	max-height: 88px;
	max-width: 320px;
	object-fit: contain;
`;

const ConfigCard = styled(Box)`
	border: 1px solid ${tokens.border.subtle};
	border-radius: 6px;
	background: ${(props) => props.theme.colors.white};
	padding: 20px 24px;
`;

const ConfigRow = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	padding: 10px 0;
	gap: 16px;
`;

const ConfigLabel = styled.span`
	color: ${tokens.text.secondary};
	opacity: 0.7;
	font-size: 14px;
`;

const ConfigValue = styled.span`
	color: ${tokens.text.secondary};
	font-size: 14px;
	font-weight: 500;
	text-align: right;
`;

const ActivePill = styled(Pill)`
	background-color: ${(props) => props.theme.colors.green};
	color: ${(props) => props.theme.colors.white};
`;

const InactivePill = styled(Pill)`
	background-color: ${tokens.border.subtle};
	color: ${tokens.text.secondary};
`;

function Description() {
	const { t } = useTranslation();

	return (
		<>
			<Paragraph mb={2}>{t('settings.connectors.goodHabitz.description.intro')}</Paragraph>
			<Paragraph mb={3}>{t('settings.connectors.goodHabitz.description.sync')}</Paragraph>
			<Heading3>{t('settings.connectors.goodHabitz.description.benefitsTitle')}</Heading3>
			<Paragraph>
				<UnorderedList>
					{t('settings.connectors.goodHabitz.benefits')
						.split('\n')
						.map((benefit) => benefit.trim())
						.filter(Boolean)
						.map((benefit) => (
							<li key={benefit}>{benefit}</li>
						))}
				</UnorderedList>
			</Paragraph>
		</>
	);
}

/**
 * Customer-facing GoodHabitz integrations page. Workspace superusers configure the
 * connector (API key, enabled, languages) here; other users see read-only status and
 * can trigger sync when active. Gated by the GOODHABITZ feature flag.
 */
export default function GoodHabitz() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { meta } = useLocalize();
	const { isWorkspaceSuperuser } = useViewerRoles();
	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);

	const query = graphql.useViewerGoodHabitzConfigQuery({
		fetchPolicy: 'cache-and-network',
		skip: goodHabitzFeature.loading || !goodHabitzFeature.available,
	});

	const remoteConfig = useMemo<GoodHabitzConfig | null>(() => {
		const config = query.data?.viewer?.workspace?.goodHabitzConfig;

		if (!config) {
			return null;
		}

		return {
			enabled: config.enabled,
			languageIDs: [...config.languageIDs],
			hasApiKey: config.hasApiKey,
			lastSyncedAt: config.lastSyncedAt,
		};
	}, [query.data]);

	const [config, setConfig] = useState<GoodHabitzConfig>({
		enabled: remoteConfig?.enabled ?? false,
		languageIDs: remoteConfig?.languageIDs ?? [],
		hasApiKey: remoteConfig?.hasApiKey ?? false,
		lastSyncedAt: remoteConfig?.lastSyncedAt,
	});
	const [apiKey, setApiKey] = useState('');

	const updateConfig = useCallback((patch: Partial<GoodHabitzConfig>) => {
		setConfig((prev) => ({ ...prev, ...patch }));
	}, []);

	useEffect(() => {
		setConfig({
			enabled: remoteConfig?.enabled ?? false,
			languageIDs: remoteConfig?.languageIDs ?? [],
			hasApiKey: remoteConfig?.hasApiKey ?? false,
			lastSyncedAt: remoteConfig?.lastSyncedAt,
		});
		setApiKey('');
	}, [
		remoteConfig?.enabled,
		remoteConfig?.languageIDs,
		remoteConfig?.hasApiKey,
		remoteConfig?.lastSyncedAt,
	]);

	const languageOptions = useMemo(
		() =>
			_.sortBy(
				meta.treamsLanguages.map((value) => ({
					title: t(`common.language.${value}`),
					value,
				})),
				'title',
			),
		[meta.treamsLanguages, t],
	);

	const [save, { loading: saving, error: saveError }] = graphql.useSetGoodHabitzConfigMutation();
	const [syncGoodHabitz, { loading: syncing }] = graphql.useSyncGoodHabitzMutation();

	const handleSave = useCallback(async () => {
		try {
			await save({
				variables: {
					input: {
						enabled: config.enabled,
						languageIDs: config.languageIDs,
						...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
					},
				},
				refetchQueries: ['ViewerGoodHabitzConfig'],
			});
			setApiKey('');
			addToast(t('common.saved'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			// Error surfaced via saveError.
		}
	}, [save, config.enabled, config.languageIDs, apiKey, addToast, t]);

	const handleSync = async () => {
		try {
			await syncGoodHabitz({
				refetchQueries: ['ViewerGoodHabitzConfig'],
			});
			addToast(t('settings.connectors.goodHabitz.sync.started'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			addToast(t('settings.connectors.goodHabitz.sync.failed'), {
				appearance: 'error',
				autoDismiss: true,
			});
		}
	};

	const dirty =
		config.enabled !== (remoteConfig?.enabled ?? false) ||
		!_.isEqual([...config.languageIDs].sort(), [...(remoteConfig?.languageIDs ?? [])].sort()) ||
		!!apiKey.trim();

	const canSave = isWorkspaceSuperuser && !saving && dirty && !query.loading;

	const activeLabel = t('settings.connectors.goodHabitz.status.active');
	const inactiveLabel = t('settings.connectors.goodHabitz.status.inactive');

	const languagesDisplay = config.languageIDs.length
		? config.languageIDs
				.map((languageID) =>
					meta.treamsLanguages.includes(languageID)
						? t(`common.language.${languageID}`)
						: languageID,
				)
				.join(', ')
		: t('settings.connectors.goodHabitz.config.allLanguages');

	const lastSyncedAtDisplay = config.lastSyncedAt ? formatLastSyncedAt(config.lastSyncedAt) : null;

	if (goodHabitzFeature.loading) {
		return null;
	}

	if (!goodHabitzFeature.available) {
		return <Redirect to="/settings/integrations" />;
	}

	return (
		<Container py={4}>
			<Banner mb={3}>
				<BannerLogo src={GOODHABITZ_LOGO_URL} alt="GoodHabitz" />
			</Banner>

			<Flex mb={3} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					GoodHabitz
				</Heading>
				<Box flexGrow={0} flexShrink={0}>
					{config.enabled ? (
						<ActivePill small>{activeLabel}</ActivePill>
					) : (
						<InactivePill small>{inactiveLabel}</InactivePill>
					)}
				</Box>
			</Flex>

			<Flex mb={4} style={{ gap: 24 }}>
				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.goodHabitz.description.heading')}</Heading3>
					<Separator mt={2} mb={3} />
					<Description />
					<Paragraph mb={2}>
						<a
							href="https://www.goodhabitz.com/"
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'underline' }}
						>
							{t('settings.connectors.goodHabitz.viewWebsite')}
						</a>
					</Paragraph>
				</Box>

				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.goodHabitz.config.title')}</Heading3>
					<Separator mt={2} mb={3} />
					{saveError && (
						<Box mb={2}>
							<GraphQLErrorMessage error={saveError.graphQLErrors?.[0] ?? saveError} />
						</Box>
					)}
					<ConfigCard>
						{isWorkspaceSuperuser ? (
							<>
								<Flex alignItems="center" mb={2}>
									<Box flexGrow={1}>
										<Paragraph>{t('settings.connectors.goodHabitz.config.enabled')}</Paragraph>
									</Box>
									<Switch
										on={config.enabled}
										onChange={(on: boolean) => updateConfig({ enabled: on })}
									/>
								</Flex>
								<Box mb={2}>
									<Paragraph mb={1}>{t('settings.connectors.goodHabitz.config.apiKey')}</Paragraph>
									<Input
										type="password"
										value={apiKey}
										onChange={setApiKey}
										placeholder={
											config.hasApiKey
												? t('settings.connectors.goodHabitz.config.apiKey.placeholder.replace')
												: t('settings.connectors.goodHabitz.config.apiKey.placeholder.set')
										}
										autoComplete="off"
									/>
								</Box>
								<Box mb={2}>
									<Paragraph mb={1}>
										{t('settings.connectors.goodHabitz.config.languages')}
									</Paragraph>
									<MultiSelect
										selectedOptions={languageOptions.filter(({ value }) =>
											config.languageIDs.includes(value),
										)}
										options={languageOptions}
										onChange={(languageIDs) => updateConfig({ languageIDs })}
									/>
								</Box>
								{config.lastSyncedAt && (
									<Paragraph mb={2} small color="grey">
										{t('settings.connectors.goodHabitz.config.lastSynced')}
										{': '}
										{lastSyncedAtDisplay}
									</Paragraph>
								)}
								<Flex justifyContent="flex-end" mb={2}>
									<Button
										small
										type="primary"
										title={t('common.save')}
										disabled={!canSave}
										loading={saving}
										onClick={handleSave}
									/>
								</Flex>
							</>
						) : (
							<>
								<ConfigRow>
									<ConfigLabel>{t('settings.connectors.goodHabitz.config.status')}</ConfigLabel>
									<ConfigValue>
										{config.enabled ? (
											<ActivePill small>{activeLabel}</ActivePill>
										) : (
											<InactivePill small>{inactiveLabel}</InactivePill>
										)}
									</ConfigValue>
								</ConfigRow>
								<ConfigRow>
									<ConfigLabel>{t('settings.connectors.goodHabitz.config.languages')}</ConfigLabel>
									<ConfigValue>{remoteConfig ? languagesDisplay : '—'}</ConfigValue>
								</ConfigRow>
								<ConfigRow>
									<ConfigLabel>{t('settings.connectors.goodHabitz.config.lastSynced')}</ConfigLabel>
									<ConfigValue>
										{lastSyncedAtDisplay || t('settings.connectors.goodHabitz.config.neverSynced')}
									</ConfigValue>
								</ConfigRow>
								{!remoteConfig && (
									<Paragraph small mt={2}>
										{t('settings.connectors.goodHabitz.config.notConfigured')}
									</Paragraph>
								)}
							</>
						)}

						<Separator my={3} />

						<Flex justifyContent="flex-end">
							<Button
								type="primary"
								title={t('settings.connectors.goodHabitz.syncNow')}
								loading={syncing}
								disabled={!config.enabled || syncing}
								onClick={handleSync}
							/>
						</Flex>
					</ConfigCard>
				</Box>
			</Flex>
		</Container>
	);
}

export function GoodHabitzTile(props: { selected: boolean }) {
	const { t } = useTranslation();
	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);

	if (goodHabitzFeature.loading || !goodHabitzFeature.available) {
		return null;
	}

	return (
		<ProductTile
			id="goodhabitz"
			title="GoodHabitz"
			description={t('settings.connectors.goodHabitz.tile.description')}
			logoImageURL={GOODHABITZ_LOGO_URL}
			selected={props.selected}
		/>
	);
}
